import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAvatarLibrary, type UserAvatar } from "@/hooks/useAvatarLibrary";
import AvatarLibraryCard from "@/components/settings/AvatarLibraryCard";
import MetaPersonAvatarFlow from "@/components/avatar/MetaPersonAvatarFlow";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { isPaymentUiBlocked } from "@/lib/platform";

// Generated MetaPerson rows carry label 'metaperson:<avatarCode>'. The catalog
// rows use 'prerendered:<id>' and are NOT editable in the Creator.
function generatedAvatarCode(avatar: UserAvatar): string | null {
  const label = avatar.label || "";
  if (avatar.provider !== "metaperson") return null;
  if (!label.startsWith("metaperson:")) return null;
  const code = label.slice("metaperson:".length).trim();
  return code || null;
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    values[key] != null ? String(values[key]) : `{${key}}`,
  );
}

const SettingsAvatars = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    avatars,
    count,
    limit,
    canAddAvatar,
    isPremium,
    setActive,
    renameAvatar,
    deleteAvatar,
    refetch,
    loading,
  } = useAvatarLibrary();

  // Gate do "Editar rosto" (Creator EMBUTIDO / metaperson-token) — flag de
  // CRIAÇÃO, não o METAPERSON_ENABLED de render (separados em 24/08, finding
  // 831ad829: o de render precisa ficar true ou o avatar salvo de 1.556
  // pessoas cai num fallback que 404).
  const metapersonCreateOn = useFeatureFlag("METAPERSON_CREATE_ENABLED");
  const canGenerate = metapersonCreateOn && isPremium;
  // null = closed; { code? } = flow open (code set when editing an existing
  // generated avatar).
  const [flow, setFlow] = useState<{ avatarCodeToEdit?: string } | null>(null);

  const counterText = isPremium
    ? interpolate(t("settings.avatars.counter.premium"), { count })
    : interpolate(t("settings.avatars.counter.free"), { count, limit });

  const handleNewAvatar = () => {
    if (!canAddAvatar) {
      toast(t("settings.avatars.limitToast"), {
        action: isPaymentUiBlocked() ? undefined : {
          label: t("settings.avatars.limitToastAction"),
          onClick: () => navigate("/pricing"),
        },
      });
      return;
    }
    // Always open the choice screen (criar próprio / catálogo / sem avatar)
    // instead of jumping straight into the MetaPerson Creator. Coming from
    // Settings the user has a completed profile, so OnboardingAvatar's handlers
    // route to /processing, which detects edit-mode (onboarding_complete=true),
    // patches only the avatar fields, and lands on /home (the Mirror) — it never
    // re-runs first-time onboarding. Editing an EXISTING generated avatar still
    // opens the Creator directly via the per-card onEdit path below.
    // Mark this as an avatar EDIT entry so OnboardingAvatar does NOT fire
    // onboarding_started — the user already completed onboarding (this routes
    // to Processing's edit-mode). Genuine first-avatar entries (Mirror CTA,
    // deep-link) carry no state and still count.
    navigate("/onboarding/avatar", { state: { mode: "edit" } });
  };

  const handleFlowComplete = async () => {
    setFlow(null);
    try {
      await refetch();
    } catch (err) {
      console.error("[SettingsAvatars] refetch after flow failed:", err);
    }
  };

  const handleSetActive = async (id: string) => {
    try {
      await setActive(id);
    } catch (err) {
      console.error("[SettingsAvatars] setActive failed:", err);
      toast.error(t("common.error"));
    }
  };

  const handleRename = async (id: string, label: string) => {
    try {
      await renameAvatar(id, label);
    } catch (err) {
      console.error("[SettingsAvatars] rename failed:", err);
      toast.error(t("common.error"));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAvatar(id);
    } catch (err) {
      console.error("[SettingsAvatars] delete failed:", err);
      toast.error(t("common.error"));
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "hsl(222 38% 6%)" }}
    >
      {/* MetaPerson Creator flow overlay (consent → token → iframe → import).
          The iframe phase renders its own full-screen layer; the consent /
          spinner phases render inside this padded overlay. */}
      {flow && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto px-5 pt-16 pb-10"
          style={{ background: "hsl(222 38% 6%)" }}
        >
          <MetaPersonAvatarFlow
            avatarCodeToEdit={flow.avatarCodeToEdit}
            onComplete={handleFlowComplete}
            onCancel={() => setFlow(null)}
          />
        </div>
      )}

      {/* Header */}
      <div
        className="flex items-center gap-3 px-5 pb-4"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px) + 1rem, 3.5rem)" }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[13px] text-foreground/60 hover:text-foreground transition-colors"
        >
          <ChevronLeft size={16} />
          {t("common.back")}
        </button>
        <h1 className="flex-1 text-center text-[15px] font-display font-semibold text-foreground pr-10">
          {t("settings.avatars.title")}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-12 space-y-6">
        {/* Intro row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-between"
        >
          <span
            className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-display font-semibold"
            style={{
              background: "hsl(187 72% 50% / 0.12)",
              color: "hsl(187 72% 70%)",
              border: "1px solid hsl(187 72% 50% / 0.25)",
            }}
          >
            {counterText}
          </span>
        </motion.div>

        {/* + Novo avatar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <button
            type="button"
            onClick={handleNewAvatar}
            disabled={!canAddAvatar}
            className="w-full py-3.5 rounded-2xl text-[14px] font-display font-semibold transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            style={{
              background: canAddAvatar
                ? "hsl(187 72% 50% / 0.18)"
                : "hsl(222 18% 18% / 0.6)",
              border: canAddAvatar
                ? "1px solid hsl(187 72% 50% / 0.3)"
                : "1px solid hsl(222 18% 25% / 0.6)",
              color: canAddAvatar ? "hsl(187 72% 60%)" : "hsl(210 20% 50%)",
              boxShadow: canAddAvatar ? "0 0 20px hsl(187 72% 50% / 0.12)" : "none",
            }}
          >
            <Plus size={14} />
            {t("settings.avatars.newAvatar")}
          </button>

          {!canAddAvatar && !isPremium && (
            <p className="mt-2 text-[11px] font-body text-foreground/55 text-center">
              {t("settings.avatars.limitReached")}{" "}
              {!isPaymentUiBlocked() && (
                <button
                  type="button"
                  onClick={() => navigate("/pricing")}
                  className="underline"
                  style={{ color: "hsl(187 72% 70%)" }}
                >
                  {t("settings.avatars.limitReachedCta")}
                </button>
              )}
            </p>
          )}
        </motion.div>

        {/* Grid or empty state */}
        {!loading && avatars.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="rounded-2xl p-6 text-center space-y-3"
            style={{
              background: "hsl(222 30% 11%)",
              border: "1px dashed hsl(222 18% 25% / 0.6)",
            }}
          >
            <p className="text-[13px] font-display font-semibold text-foreground/80">
              {t("settings.avatars.empty")}
            </p>
            <button
              type="button"
              onClick={() => navigate("/onboarding/avatar", { state: { mode: "edit" } })}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-display font-semibold"
              style={{
                background: "hsl(187 72% 50%)",
                color: "hsl(222 38% 6%)",
              }}
            >
              <Plus size={12} />
              {t("settings.avatars.emptyAction")}
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="grid grid-cols-2 gap-4"
          >
            {avatars.map((a) => {
              const editCode = canGenerate ? generatedAvatarCode(a) : null;
              return (
                <AvatarLibraryCard
                  key={a.id}
                  avatar={a}
                  isLastAvatar={avatars.length === 1}
                  onSetActive={() => handleSetActive(a.id)}
                  onRename={(newLabel) => handleRename(a.id, newLabel)}
                  onDelete={() => handleDelete(a.id)}
                  onEdit={editCode ? () => setFlow({ avatarCodeToEdit: editCode }) : undefined}
                />
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SettingsAvatars;
